import { twMerge } from "tailwind-merge";

const variants = {
    page: {
        diameter: 100,
        strokeWidth: 4,
    },
    panel: {
        diameter: 48,
        strokeWidth: 4,
    },
    inline: {
        diameter: 16,
        strokeWidth: 1.5,
    },
    inlineSmall: {
        diameter: 12,
        strokeWidth: 1.5,
    },
};

const colors = {
    bronze: "var(--color-bronze-600)",
    stone: "var(--color-stone-500)",
    indigo: "var(--color-indigo-600)",
};

interface SpinnerProps {
    variant?: keyof typeof variants;
    color?: keyof typeof colors;
    className?: string;
}

const Spinner = ({ variant = "panel", color = "bronze", className = "" }: SpinnerProps) => {
    // Select the color property base on the color prop
    const colorSelected = colors[color];

    // Select the variant properties based on the variant prop
    const { diameter, strokeWidth } = variants[variant];

    // The radius is the diameter divided by 2 minus half the stroke width to get the center of the line
    const radius = diameter / 2 - strokeWidth / 2;

    // The circumference is the total length of the circle calculated as 2 * Math.PI * radius
    const circumference = 2 * Math.PI * radius;

    // The sweep is the angle of the arc that we want to show
    const sweep = 0.2; // 20%

    // The arc length is the length of the bronze part of the spinner
    const arcLength = circumference * sweep;

    // The dash array is used to create the stroke effect
    const dashArray = `${arcLength} ${circumference}`;

    return (
        <div
            className={twMerge("relative flex items-center justify-center", className)}
            style={{ width: diameter, height: diameter }}
            aria-label="Loading"
        >
            <div className="flex animate-spin will-change-transform [animation-duration:0.9s]">
                <svg width={diameter} height={diameter} viewBox={`0 0 ${diameter} ${diameter}`} className="block">
                    {/* Base circle */}
                    <circle cx={diameter / 2} cy={diameter / 2} r={radius} fill="none" stroke="var(--color-stone-200)" strokeWidth={strokeWidth} />
                    {/* Bronze arc */}
                    <g
                        style={{
                            transform: "rotate(-90deg)",
                            WebkitTransform: "rotate(-90deg)",
                            transformOrigin: "50% 50%",
                            WebkitTransformOrigin: "50% 50%",
                        }}
                    >
                        <circle
                            cx={diameter / 2}
                            cy={diameter / 2}
                            r={radius}
                            fill="none"
                            stroke={colorSelected}
                            strokeWidth={strokeWidth}
                            strokeDasharray={dashArray}
                            strokeDashoffset={0}
                            strokeLinecap="round"
                        />
                    </g>
                </svg>
            </div>
        </div>
    );
};

export default Spinner;
